import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { FLEET_AVAILABILITY_GROUPS, type FleetAvailabilityGroup, type TmsFleetAvailability } from '@numeo/types-main-web-app'
import { Badge, Button, type BadgeType } from '@/components/ds2'
import { useGeneralAgentChatStore } from '../../hooks/generalAgentChatStore'
import type { FleetTruck } from '../../api/assignDriverApi'
import { fleetBucketsOf, hasInactiveDriver, locationOf, readyWordsOf } from './assignModel'
import { ReverseAssignPicker } from './ReverseAssignPicker'
import { useCanAssignLoads } from './useCanAssignLoads'

/** The group headings, as the answer names them. */
const GROUP_LABEL: Record<FleetAvailabilityGroup, string> = {
    ready_now: 'Ready now',
    ready_later: 'Ready later today',
    on_load: 'On a load',
    off: 'Home time / off',
    out_of_service: 'Shop / out of service',
}

/** Groups a truck can be assigned from: free now or later today. */
const ASSIGNABLE_GROUPS: ReadonlySet<FleetAvailabilityGroup> = new Set(['ready_now', 'ready_later'])

/** Only the free groups start open; every other group starts closed, its count in the header. */
const STARTS_OPEN: ReadonlySet<FleetAvailabilityGroup> = new Set(['ready_now', 'ready_later'])

/** A blocking driver status's end, as the row shows it: "until 12:00". */
const untilWords = (iso: string | undefined): string => {
    if (!iso) return ''
    const at = new Date(iso)
    return Number.isNaN(at.getTime()) ? '' : `until ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`
}

/** The status badge of a truck in its group: its free time, what keeps it off, or its shop status. An inactive driver says so first. */
const statusOf = (truck: FleetTruck, group: FleetAvailabilityGroup): { type: BadgeType; label: string } => {
    if (hasInactiveDriver(truck)) return { type: 'warning', label: 'Driver inactive' }
    if (group === 'off') {
        const off = truck.drivers.find((driver) => driver.off)?.off
        return { type: 'warning', label: off ? `${off.status_name} ${untilWords(off.ends_at)}`.trim() : 'Off' }
    }
    if (group === 'out_of_service') return { type: 'danger', label: truck.operational_status ?? 'Out of service' }
    if (group === 'on_load') {
        const ready = readyWordsOf(truck.ready_at)
        const label = [truck.current_load ? `On #${truck.current_load.load_number}` : 'On a load', ready].filter(Boolean).join(' · ')
        return { type: 'default', label }
    }
    if (group === 'ready_now') return { type: 'success', label: 'Ready now' }
    return { type: 'default', label: readyWordsOf(truck.ready_at) ?? 'Ready time unknown' }
}

/** The drivers' names; a team reads both. */
const driversWordsOf = (truck: FleetTruck): string => {
    if (truck.no_driver || truck.drivers.length === 0) return 'No driver'
    return truck.drivers.map((driver) => driver.name).join(' · ')
}

interface FleetTruckRowProps {
    truck: FleetTruck
    group: FleetAvailabilityGroup
    tabId: string | null
    canAssign: boolean
}

/** One truck, on fixed columns: its number and drivers, its status badge, where it is, and "Assign to load" for a truck free to take one. */
const FleetTruckRow = ({ truck, group, tabId, canAssign }: FleetTruckRowProps) => {
    const [assigning, setAssigning] = useState(false)
    const assignable = canAssign && !!tabId && ASSIGNABLE_GROUPS.has(group) && !hasInactiveDriver(truck)
    const status = statusOf(truck, group)
    const position = locationOf(truck)
    return (
        <li className="flex flex-col gap-2 border-t border-border py-2 first:border-t-0">
            <div className="grid grid-cols-[minmax(0,1fr)_11rem_7rem_auto] items-center gap-x-3 gap-y-1">
                <span className="min-w-0">
                    <span className="block truncate font-medium">Truck {truck.truck_number}</span>
                    <span className="block truncate text-foreground-muted">{driversWordsOf(truck)}</span>
                </span>
                <span className="min-w-0">
                    <Badge variant="soft" type={status.type} size="sm" label={status.label} />
                </span>
                <span title={position.source} className={`min-w-0 truncate ${position.muted ? 'text-foreground-muted' : ''}`}>
                    {position.label}
                </span>
                {assignable ? (
                    <Button size="sm" variant="secondary" aria-expanded={assigning} onClick={() => setAssigning(!assigning)}>
                        Assign to load
                    </Button>
                ) : (
                    <span />
                )}
            </div>
            {assigning && tabId && <ReverseAssignPicker tabId={tabId} truck={truck} onClose={() => setAssigning(false)} />}
        </li>
    )
}

/**
 * "Who's free": the company's trucks by group, each with its drivers, status and where it is. Ready now and ready later start open;
 * every other group starts closed, with its count. A truck with an inactive driver is home time and ranked last there.
 */
export const FleetView = ({ fleet }: { fleet: TmsFleetAvailability }) => {
    const tabId = useGeneralAgentChatStore((state) => state.tabId)
    const canAssign = useCanAssignLoads()
    const [open, setOpen] = useState<ReadonlySet<FleetAvailabilityGroup>>(() => STARTS_OPEN)
    const buckets = fleetBucketsOf(fleet.groups)
    const toggle = (group: FleetAvailabilityGroup) => {
        const next = new Set(open)
        if (next.has(group)) next.delete(group)
        else next.add(group)
        setOpen(next)
    }
    const groups = FLEET_AVAILABILITY_GROUPS.filter((group) => buckets[group].length > 0)

    return (
        <div aria-label="Who's free" className="flex flex-col gap-3 text-xs text-foreground">
            {groups.length === 0 && <p className="text-foreground-muted">No trucks in this company.</p>}
            {groups.map((group) => {
                const trucks = buckets[group]
                const isOpen = open.has(group)
                return (
                    <section key={group} aria-label={GROUP_LABEL[group]} className="flex flex-col">
                        <button
                            type="button"
                            aria-expanded={isOpen}
                            onClick={() => toggle(group)}
                            className="flex items-center gap-1.5 py-1 text-left font-semibold text-foreground-muted"
                        >
                            {isOpen ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
                            {`${GROUP_LABEL[group]} · ${trucks.length}`}
                        </button>
                        {isOpen && (
                            <ul className="flex flex-col">
                                {trucks.map((truck) => (
                                    <FleetTruckRow key={truck.truck_id} truck={truck} group={group} tabId={tabId} canAssign={canAssign} />
                                ))}
                            </ul>
                        )}
                    </section>
                )
            })}
            <footer className="text-foreground-muted">{fleet.truncated ? 'Some loads were cut short: a truck’s group may not be certain.' : undefined}</footer>
        </div>
    )
}
